import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { useDisplayCurrency } from '../../context/displayCurrencyContext';
import { DISPLAY_CURRENCY_EUR, DISPLAY_CURRENCY_USD } from '../../util/displayCurrency';

import css from './CurrencySwitcher.module.css';

const CURRENCY_MESSAGE_IDS = {
  [DISPLAY_CURRENCY_EUR]: 'CurrencySwitcher.eur',
  [DISPLAY_CURRENCY_USD]: 'CurrencySwitcher.usd',
};

/**
 * EUR | USD toggle for buyer display currency preference.
 * Desktop uses a segmented control; mobile can use a compact single-button alternate switch.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {'segmented'|'compact'} [props.variant='segmented']
 * @returns {JSX.Element}
 */
const CurrencySwitcher = props => {
  const { className, variant = 'segmented' } = props;
  const intl = useIntl();
  const { displayCurrency, setDisplayCurrency } = useDisplayCurrency();

  const groupLabel = intl.formatMessage({ id: 'CurrencySwitcher.label' });

  if (variant === 'compact') {
    const alternateCurrency =
      displayCurrency === DISPLAY_CURRENCY_EUR ? DISPLAY_CURRENCY_USD : DISPLAY_CURRENCY_EUR;
    const alternateLabel = intl.formatMessage({ id: CURRENCY_MESSAGE_IDS[alternateCurrency] });

    return (
      <button
        type="button"
        className={classNames(css.compact, className)}
        aria-label={intl.formatMessage(
          { id: 'CurrencySwitcher.switchTo' },
          { currency: alternateLabel }
        )}
        onClick={() => setDisplayCurrency(alternateCurrency)}
      >
        {alternateLabel}
      </button>
    );
  }

  return (
    <div
      className={classNames(css.root, className)}
      role="group"
      aria-label={groupLabel}
    >
      <button
        type="button"
        className={classNames(css.option, {
          [css.optionActive]: displayCurrency === DISPLAY_CURRENCY_EUR,
        })}
        aria-pressed={displayCurrency === DISPLAY_CURRENCY_EUR}
        onClick={() => setDisplayCurrency(DISPLAY_CURRENCY_EUR)}
      >
        {intl.formatMessage({ id: CURRENCY_MESSAGE_IDS[DISPLAY_CURRENCY_EUR] })}
      </button>
      <button
        type="button"
        className={classNames(css.option, {
          [css.optionActive]: displayCurrency === DISPLAY_CURRENCY_USD,
        })}
        aria-pressed={displayCurrency === DISPLAY_CURRENCY_USD}
        onClick={() => setDisplayCurrency(DISPLAY_CURRENCY_USD)}
      >
        {intl.formatMessage({ id: CURRENCY_MESSAGE_IDS[DISPLAY_CURRENCY_USD] })}
      </button>
    </div>
  );
};

export default CurrencySwitcher;
